'use client';

import { useLayoutEffect } from 'react';
import { usePathname } from 'next/navigation';
import { poolForRoute, type AuthPool } from '@/lib/auth-pool';
import { SessionProvider } from '@/lib/session/SessionProvider';
import { IdleTimeoutManager } from '@/components/session/IdleTimeoutManager';
import { redirectIfTabDisplaced, alignAuthenticatedPoolWithRoute } from '@/lib/session/tab-sync';

/**
 * Routes that must never mount idle handling, because they are where an
 * idle-expired user is *sent*. Enforcing here would sign the user out of the page
 * that exists to sign them back in — a redirect loop.
 *
 * Matched as exact paths or path prefixes; `/tenant/{name}/login` is covered by the
 * `endsWith('/login')` check rather than by listing every tenant.
 */
const UNAUTHENTICATED_PREFIXES = [
    '/login',
    '/logout-complete',
    '/logout-confirmation',
    '/system-use-notice',
    '/debug-auth',
] as const;

function isUnauthenticatedRoute(pathname: string): boolean {
    if (pathname.endsWith('/login')) return true;
    return UNAUTHENTICATED_PREFIXES.some(
        (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    );
}

/**
 * Which pool's idle clock this route's heartbeats belong to. Sent as
 * SESSION_POOL_HEADER; see lib/session/constants.ts for the full rationale.
 *
 * Re-exported rather than defined here: proxy.ts needs the same mapping and cannot
 * import a `'use client'` module. See lib/auth-pool.ts.
 */
export { poolForRoute };

/**
 * Seed for the route's pool: its stamp, or `0` — epoch, i.e. unambiguously expired —
 * when that pool holds a session but has no stamp.
 *
 * `undefined` reaches `SessionProvider` as "start the clock at now", which is the
 * wrong reading of an absent stamp. Both sign-in callbacks write one before they
 * redirect and `/api/employee/init` only ever preserves it, so on a live session
 * absent means it was CLEARED by an idle 401 or outlived its 8-hour maxAge — either
 * way the session is over. `/employee/tenants` is where that matters: it never calls
 * an init route and the proxy returns early for HTML navigations (`isAuthApiPath`),
 * so this client clock is the only idle enforcement on the authenticated tenant
 * picker, and reading absent as fresh left an already-expired employee session able
 * to keep switching cells. `0` is the same reading the proxy and both init routes
 * already apply (`?? 0`).
 *
 * Only when that pool HAS a session. A customer-only user who lands on `/employee/*`
 * resolves to the employee pool and will never have an employee stamp, and the
 * sign-out this triggers is `scope=local`, which clears BOTH pools — so failing
 * closed on a pool they never held would sign them out of a perfectly good customer
 * session. When the caller cannot say (no `hasSessionByPool`), keep the old reading
 * rather than guess.
 *
 * `freshLoginByPool` overrides both readings, and does so BEFORE the stamp is
 * consulted so a stale stamp cannot outvote a login that just happened — the same
 * precedence the server applies in `effectiveLastActivityMs`. Without it, a login
 * whose stamp never landed painted the lock screen on its first frame and the user
 * could not get in at all; see `readFreshLoginPools` in app/layout.tsx. The clock
 * latches expired, so this seed is not a transient the first request would correct:
 * only a 2xx from /api/session/extend can clear it.
 */
function seedActivityFor(
    pool: AuthPool,
    initialActivityByPool?: { customer?: number; employee?: number },
    hasSessionByPool?: { customer?: boolean; employee?: boolean },
    freshLoginByPool?: { customer?: boolean; employee?: boolean },
): number | undefined {
    // `undefined` reaches SessionProvider as "start the clock at now".
    if (freshLoginByPool?.[pool]) return undefined;
    const stamp = initialActivityByPool?.[pool];
    if (typeof stamp === 'number') return stamp;
    return hasSessionByPool?.[pool] ? 0 : undefined;
}

/**
 * Mounts the 30-minute idle warning banner and automatic sign-out for every
 * authenticated route.
 *
 * TR-3393: this used to live inside `app/page.tsx`, which made it the *only* route
 * with any client-side idle handling — `/employee/app`, `/analytics`,
 * `/immigration-reports`, `/admin`, `/meta-viewer`, `/email-viewer` and
 * `/xlsx-viewer` had neither a warning nor a sign-out. Mounting it from the root
 * layout is what makes the policy hold on every route, and means a route added
 * later is covered by default instead of silently exempt.
 *
 * `hasSession` is resolved server-side by the root layout from the auth cookies, so
 * an anonymous visitor never runs the idle machinery at all.
 *
 * `initialActivityByPool` carries BOTH pools' stamps rather than one pre-resolved
 * value, because the pool is a property of the route and only this component knows
 * the route. The layout used to collapse them with `Math.max`, which let a CSM's live
 * customer session hold the employee clock open on `/employee/*` — see
 * `readSeedActivity` in app/layout.tsx (TR-3393).
 *
 * `hasSessionByPool` is the per-pool refinement of `hasSession`, needed only to read
 * a MISSING stamp correctly — see `seedActivityFor`. `freshLoginByPool` is the
 * exemption that keeps that strict reading from locking out a session created
 * seconds ago, resolved from the httpOnly access token the client cannot read.
 */
export function SessionMount({
    initialActivityByPool,
    hasSession,
    hasSessionByPool,
    freshLoginByPool,
}: {
    initialActivityByPool?: { customer?: number; employee?: number };
    hasSession: boolean;
    hasSessionByPool?: { customer?: boolean; employee?: boolean };
    freshLoginByPool?: { customer?: boolean; employee?: boolean };
}) {
    const pathname = usePathname() ?? '/';

    useLayoutEffect(() => {
        if (isUnauthenticatedRoute(pathname)) return;
        alignAuthenticatedPoolWithRoute(poolForRoute(pathname), pathname);
        redirectIfTabDisplaced();
    }, [pathname]);

    if (!hasSession || isUnauthenticatedRoute(pathname)) {
        return null;
    }

    const pool = poolForRoute(pathname);

    return (
        <SessionProvider
            initialActivityAt={seedActivityFor(
                pool,
                initialActivityByPool,
                hasSessionByPool,
                freshLoginByPool,
            )}
            pool={pool}
        >
            <IdleTimeoutManager />
        </SessionProvider>
    );
}
